import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import CustomCalendar from '../components/CustomCalendar';
import { addDays } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeekendCoursePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(null);
  const [numDays, setNumDays] = useState(2);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const INSTRUCTOR_ID = 'julie-christiansen';
  const PRICE_PER_DAY = 1200;

  const handleSubmit = async () => {
    if (!startDate) {
      toast.error('Please select a start date');
      return;
    }

    if (!formData.client_name || !formData.client_email || !formData.client_phone) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const courseData = {
        instructor_id: INSTRUCTOR_ID,
        start_date: startDate.toISOString().split('T')[0],
        num_days: numDays,
        language: i18n.language,
        ...formData
      };

      await axios.post(`${API}/weekend-courses`, courseData);
      toast.success('Weekend clinic request submitted! We will contact you to confirm availability and discuss details.');
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      console.error('Error submitting course request:', error);
      toast.error('Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const endDate = startDate ? addDays(startDate, numDays - 1) : null;
  const totalPrice = numDays * PRICE_PER_DAY;

  return (
    <div>
      <Navbar />
      <div className="booking-page">
        <button
          onClick={() => navigate(-1)}
          className="back-arrow-btn"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="booking-header">
          <h1 className="booking-title" data-testid="page-title">{t('Weekend Clinic with Julie')}</h1>
          <p style={{ color: '#6b6b6b' }}>{t('Host Julie at your stable or club for an intensive clinic')}</p>
        </div>

        <div className="booking-two-column">
          {/* Left Column - Calendar */}
          <div className="booking-left">
            <CustomCalendar
              availableDates={[]}
              selectedDate={startDate}
              onSelectDate={setStartDate}
            />
            {endDate && (
              <p style={{ marginTop: '1rem', color: '#8b4857', fontWeight: 500, textAlign: 'center' }}>
                {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Right Column - Information and Booking Form */}
          <div className="booking-right">
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', marginBottom: '1rem', fontFamily: "'Cormorant Garamond', serif" }}>
                {t('About Weekend Clinics')}
              </h2>
              <div style={{ color: '#6b6b6b', lineHeight: '1.7' }}>
                <p><strong>{t('Julie Christiansen - accomplished')}</strong></p>
                <br />
                <p>
                  • {t('3x World Champion')}<br />
                  • {t('3x Nordic Champion')}<br />
                  • {t('26x Danish Champion')}
                </p>
                <br />
                <p>{t('Host Julie intensive')}</p>
                <br />
                <p>
                  <strong>{t('Clinic Details')}</strong><br />
                  • {t('Duration minimum 2 days')}<br />
                  • {t('Maximum 10 riders')}<br />
                  • {t('Price per day', { price: PRICE_PER_DAY })}<br />
                  • {t('Includes travel costs')}<br />
                  • {t('Additional accommodation')}
                </p>
                <br />
                <p>
                  <strong>{t('Important')}</strong><br />
                  • {t('Book minimum 2 days')}<br />
                  • {t('Cancellation 1 month')}<br />
                  • {t('Program tailored')}
                </p>
                <br />
                <p style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                  {t('After submitting request')}
                </p>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('Number of Days')}</label>
              <div className="payment-methods">
                {[2, 3, 4].map((days) => (
                  <div
                    key={days}
                    className={`payment-method ${numDays === days ? 'selected' : ''}`}
                    onClick={() => setNumDays(days)}
                    data-testid={`num-days-${days}`}
                  >
                    {days} {t('Days')}<br />
                    <span style={{ fontSize: '1.125rem', fontWeight: 600 }}>€{days * PRICE_PER_DAY}</span>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '0.875rem', color: '#6b6b6b', marginTop: '0.5rem' }}>{t('Plus accommodation')}</p>
              {endDate && (
                <p style={{ marginTop: '1rem', color: '#8b4857', fontWeight: 500 }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 600, display: 'block' }}>
                    {t('Total', { total: totalPrice })}
                  </span>
                  <span style={{ fontSize: '0.875rem' }}>{t('Plus accommodation')}</span>
                </p>
              )}
            </div>

            <div className="section-title" style={{ marginTop: '2rem' }}>{t('Your Information')}</div>
            
            <div className="form-group">
              <label className="form-label">{t('Full Name')}</label>
              <input
                type="text"
                className="form-input"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                data-testid="client-name-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('Email')}</label>
              <input
                type="email"
                className="form-input"
                value={formData.client_email}
                onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                data-testid="client-email-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('Phone')}</label>
              <input
                type="tel"
                className="form-input"
                value={formData.client_phone}
                onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                data-testid="client-phone-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('Stable/Club Name')}</label>
              <input
                type="text"
                className="form-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('Where clinic held')}
                data-testid="notes-input"
              />
            </div>

            <button
              className="cta-button"
              onClick={handleSubmit}
              disabled={loading}
              style={{ width: '100%', marginTop: '1rem', justifyContent: 'center' }}
              data-testid="submit-request-button"
            >
              {loading ? t('Submitting') : t('Request Weekend Clinic')}
            </button>

            <p style={{ fontSize: '0.875rem', color: '#6b6b6b', marginTop: '1rem', textAlign: 'center' }}>
              {t('Request only')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeekendCoursePage;